'use client';
import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';
import { isExternalLink } from '../../helpers/isExternalLink.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { defaultStyles } from '../../mixins/defaultStyles.js';
import { keyboardFocusShadowAfter } from '../../mixins/keyboardFocus.js';
import { spaceProp } from '../../mixins/spaceProp.js';
import { textStyle } from '../../mixins/textStyle.js';
import { useTitanRouter } from '../../router/hooks/useTitanRouter.js';

const sharedLinkStyle = ({
  theme,
  spaceAfter
}) => css(["", ";", ";", ";display:inline-block;position:relative;color:", ";text-decoration:", ";text-align:left;&:hover{color:", ";text-decoration:", ";}&:active{color:", ";text-decoration:", ";}", " &[disabled]{color:", ";text-decoration:", ";user-select:none;cursor:auto;pointer-events:none;}", " ", ";"], defaultStyles.boxSizing, defaultStyles.fontSmoothing, textStyle('label'), theme.components.link.colorPrimary, theme.components.link.textDecoration, theme.components.link.colorPrimaryHover, theme.components.link.textDecorationHover, theme.components.link.colorActive, theme.components.link.textDecorationActive, keyboardFocusShadowAfter({
  offset: -2
}), theme.components.link.colorDisabled, theme.components.link.textDecorationDisabled, switchProp('variant', {
  primary: css(["color:", ";&:hover{color:", ";}"], theme.components.link.colorPrimary, theme.components.link.colorPrimaryHover),
  secondary: css(["color:", ";&:hover{color:", ";}"], theme.components.link.colorSecondary, theme.components.link.colorSecondaryHover),
  danger: css(["color:", ";&:hover{color:", ";}&:active{color:", ";}"], theme.components.link.colorDanger, theme.components.link.colorDangerHover, theme.components.link.colorDangerActive)
}), spaceProp(spaceAfter));
const StyledAnchor = styled.a.withConfig({
  shouldForwardProp: (prop, defaultValidationFn) => defaultValidationFn(prop)
}).withConfig({
  componentId: "sc-zcybka-0"
})(["", ";"], sharedLinkStyle);
const StyledButton = styled.button.withConfig({
  componentId: "sc-zcybka-1"
})(["border:0;cursor:pointer;background:none;padding:0;", ";"], sharedLinkStyle);
const AdornmentWrapper = styled.span.withConfig({
  componentId: "sc-zcybka-2"
})(["display:inline-flex;align-items:center;margin-bottom:auto;position:relative;top:3px;", " ", ""], ({
  position,
  theme
}) => position === 'left' && css(["margin-right:", ";"], theme.components.link.iconMargin), ({
  position,
  theme
}) => position === 'right' && css(["align-self:flex-end;margin-left:", ";"], theme.components.link.iconMargin));
const Link = /*#__PURE__*/withTitan((_a, ref) => {
  var {
      to,
      className,
      prefixAdornment,
      suffixAdornment,
      variant = 'primary',
      onClick,
      disabled,
      spaceAfter,
      children,
      role,
      onBlur,
      onFocus,
      testId = 'link'
    } = _a,
    rest = __rest(_a, ["to", "className", "prefixAdornment", "suffixAdornment", "variant", "onClick", "disabled", "spaceAfter", "children", "role", "onBlur", "onFocus", "testId"]);
  const {
    linkWrapper
  } = useTitanRouter();
  const getChildren = useCallback(({
    prefixAdornment,
    suffixAdornment,
    children
  }) => jsxs(Fragment, {
    children: [prefixAdornment && jsx(AdornmentWrapper, {
      position: "left",
      children: prefixAdornment
    }), children, suffixAdornment && jsx(AdornmentWrapper, {
      position: "right",
      children: suffixAdornment
    })]
  }), []);
  const rel = typeof to === 'string' && isExternalLink(to) ? 'noopener noreferrer' : undefined;
  const target = typeof to === 'string' && isExternalLink(to) ? '_blank' : undefined;
  return to ? linkWrapper(jsx(StyledAnchor, Object.assign({
    ref: ref,
    className: className,
    "data-test-id": testId,
    disabled: disabled,
    href: to,
    rel: rel,
    role: role,
    spaceAfter: spaceAfter,
    target: target,
    variant: variant,
    onBlur: onBlur,
    onClick: onClick,
    onFocus: onFocus
  }, rest, {
    children: getChildren({
      prefixAdornment,
      suffixAdornment,
      children
    })
  }))) : jsx(StyledButton, Object.assign({
    ref: ref,
    className: className,
    "data-test-id": testId,
    disabled: disabled,
    role: role,
    spaceAfter: spaceAfter,
    type: "button",
    variant: variant,
    onBlur: onBlur,
    onClick: onClick,
    onFocus: onFocus
  }, rest, {
    children: getChildren({
      prefixAdornment,
      suffixAdornment,
      children
    })
  }));
}, {
  name: 'Link'
});

export { Link };
//# sourceMappingURL=Link.js.map
